import { useTransparencySettingsContext } from "@/components/common/transparency-sliders/transparency-settings-context";
import { useAnnotationPermissions } from "@/hooks/use-annotation-permissions";
import { ExportModeTab } from "@/modes/export-mode/export-mode-types";
import { moveToExportMode } from "@/modes/export-mode/export-mode-utils";
import { useExportModePermissions } from "@/modes/overview-mode/use-can-use-export-mode";
import { useIsPanoWithDepthAndReady } from "@/modes/walk-mode/walk-mode-hooks";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import {
  changeToolVisibility,
  setActiveSettingsMenu,
  ToolName,
  ToolVisibility,
} from "@/store/ui/ui-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  PERSISTENT_TOOLS,
  useDeactivateToolOnUnmount,
  useToggleToolVisibility,
} from "@/tools/use-toggle-tool-visibility";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import { includes } from "@faro-lotv/foundation";
import {
  isIElementGaussianSplats,
  isIElementGenericPointCloudStream,
  isIElementImg2d,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import { useEffect } from "react";
import { FlyWalkSceneActiveElement } from "../flywalk-types";

/**
 * Compute the visibility of toolbar tools
 *
 * @param activeElement the current active element
 * @param sceneFilter the current active scene filter
 * @param hasPointCloudBehind whether the active element is backed by a point cloud,
 * which can be picked through the zoom lens
 */
export function useFlyWalkModeToolVisibility(
  activeElement: FlyWalkSceneActiveElement | undefined,
  sceneFilter: SceneFilter,
  hasPointCloudBehind: boolean,
): void {
  const isPano = activeElement && isIElementImg360(activeElement);

  const { canWriteAnnotations } = useAnnotationPermissions();
  const { canUseExportMode } = useExportModePermissions();

  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const dispatch = useAppDispatch();
  const activeTool = useAppSelector(selectActiveTool);
  useEffect(() => {
    if (activeTool === ToolName.export) {
      moveToExportMode(dispatch, ExportModeTab.pointCloud, true);
    }
  }, [activeTool, dispatch]);

  // Export tool require write permission to store in the project the exported point cloud file
  useToggleToolVisibility(
    ToolName.export,
    canUseExportMode &&
      includes([SceneFilter.PointCloud, SceneFilter.Overlay], sceneFilter),
    true,
    false,
  );

  const is2DPhoto = !!activeElement && isIElementImg2d(activeElement);
  const isOrthomosaic = !!activeElement && isIElementOrthomosaic(activeElement);
  // Splats cannot be picked, so 3D tools are only offered when the point cloud that the
  // zoom lens picks on is available too.
  const isSplatsWithoutPointCloud =
    !!activeElement &&
    isIElementGaussianSplats(activeElement) &&
    !hasPointCloudBehind;

  /**
   * Allow or disallow measurement tool to be activated according to whether the active element is a point cloud
   * or a pano with depth information
   */
  const { isPanoWithDepth, isDepthReady } = useIsPanoWithDepthAndReady(
    !!activeElement && !isIElementGaussianSplats(activeElement)
      ? activeElement
      : undefined,
  );

  useToggleToolVisibility(
    ToolName.measurement,
    !is2DPhoto && !isOrthomosaic && !isSplatsWithoutPointCloud,
    true,
    isPanoWithDepth && !isDepthReady,
  );

  /** Allow or disallow annotation creation */
  useToggleToolVisibility(
    ToolName.annotation,
    canWriteAnnotations &&
      !is2DPhoto &&
      !isOrthomosaic &&
      !isSplatsWithoutPointCloud,
    true,
    isPanoWithDepth && !isDepthReady,
  );

  /** Allow or disallow manual blurring tool */
  useToggleToolVisibility(
    ToolName.manualBlurring,
    hasWritePermission,
    !!isPano,
    false,
  );

  /** Use clip scene tool */
  useToggleToolVisibility(
    ToolName.clipScene,
    sceneFilter !== SceneFilter.Pano,
    true,
    false,
  );

  const isPointCloud = !!(
    activeElement && isIElementGenericPointCloudStream(activeElement)
  );

  useToggleToolVisibility(ToolName.analysis, isPointCloud, true, false);

  // If the scene filter is different that 'photo', then the opacity settings tool
  // is visible
  useEffect(() => {
    const visible =
      sceneFilter === SceneFilter.Overlay ||
      sceneFilter === SceneFilter.Cad ||
      sceneFilter === SceneFilter.PointCloud;

    if (visible) {
      dispatch(
        changeToolVisibility({
          name: ToolName.opacity,
          visibility: ToolVisibility.visible,
        }),
      );
      return () => {
        dispatch(
          changeToolVisibility({
            name: ToolName.opacity,
            visibility: ToolVisibility.hidden,
          }),
        );
        dispatch(setActiveSettingsMenu(null));
      };
    }
  }, [sceneFilter, dispatch]);

  useToggleToolVisibility(ToolName.screenshot, true, true, false);

  const transparency = useTransparencySettingsContext();

  // Reset opacity settings when leaving the mode
  useNonExhaustiveEffect(
    () => () => {
      transparency.resetCadAndCloudOpacities();
    },
    [],
  );

  useDeactivateToolOnUnmount(PERSISTENT_TOOLS);
}
